import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Crown, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { HelperTip } from '@/types';
import { ACCOUNT_MENU_ITEMS, SETTINGS_SUB_ITEMS } from '@/config/navConfig';
import { formatDate } from '@/utils/date';
import { useTranslation } from 'react-i18next';

export const AccountLayout: React.FC = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [tips, setTips] = useState<HelperTip[]>([]);
  const [expandedTip, setExpandedTip] = useState<number | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(location.pathname === '/settings');

  const activeTab = new URLSearchParams(location.search).get('tab') || 'account';

  const displayName = (user?.role === 'HELPER' ? user?.helperName : undefined) ?? user?.username ?? user?.email ?? '';
  const locale = i18n.language === 'zh-TW' ? 'zh-HK' : 'en-US';
  const todayStr = new Date().toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
  const vipExpiry = user?.vipExpiryDate ? formatDate(user.vipExpiryDate) : '';

  useEffect(() => {
    if (user?.role !== 'HELPER') return;
    (apiService as any).helperTips.getActive().then((r: any) => {
      if (r?.success && r.data) setTips(r.data);
    }).catch(() => {});
  }, [user?.role]);

  const visibleItems = ACCOUNT_MENU_ITEMS.filter(
    item =>
      (!item.employerOnly || user?.role === 'EMPLOYER') &&
      (!item.helperOnly || user?.role === 'HELPER'),
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr] items-start">
      {/* Left menu */}
      <Card className="gap-0 p-4 lg:sticky lg:top-24">
        {/* Greeting */}
        <div className="px-2">
          <p className="text-sm text-muted-foreground">{t('accountLayout.welcome')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-2xl font-bold text-foreground truncate">{displayName}</p>
            {user?.role === 'EMPLOYER' && (user?.isVip ? (
              <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-500">
                <Crown className="h-3 w-3" />{t('accountLayout.vipUser')}{vipExpiry && t('accountLayout.vipExpiry', { date: vipExpiry })}
              </Badge>
            ) : (
              <Badge variant="secondary">{t('accountLayout.regularUser')}</Badge>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{t('accountLayout.today', { date: todayStr })}</p>
        </div>

        <Separator className="my-3" />

        {/* Menu */}
        <nav className="space-y-1">
          {visibleItems.map(({ path, label, labelKey, icon: Icon }) => {
            const isSettings = path === '/settings';
            if (isSettings) {
              return (
                <div key={path}>
                  <button
                    type="button"
                    onClick={() => setIsSettingsOpen(prev => !prev)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                      isSettingsOpen ? 'text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="h-4 w-4 shrink-0" />
                      {t(labelKey, label)}
                    </span>
                    {isSettingsOpen
                      ? <ChevronUp className="h-3.5 w-3.5 shrink-0" />
                      : <ChevronDown className="h-3.5 w-3.5 shrink-0" />}
                  </button>
                  {isSettingsOpen && (
                    <div className="ml-7 space-y-0.5 mt-0.5">
                      {SETTINGS_SUB_ITEMS.map(sub => (
                        <NavLink
                          key={sub.tab}
                          to={`/settings?tab=${sub.tab}`}
                          className={cn(
                            'block rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                            activeTab === sub.tab && location.pathname === '/settings'
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                          )}
                        >
                          {t(sub.labelKey, sub.label)}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={path}
                to={path}
                end
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {t(labelKey, label)}
              </NavLink>
            );
          })}
        </nav>

        {/* Helper 專屬：清潔及衛生小貼士 */}
        {user?.role === 'HELPER' && tips.length > 0 && (
          <>
            <Separator className="my-3" />
            <div className="px-1">
              <div className="flex items-center gap-2 px-2 mb-2">
                <Lightbulb className="h-4 w-4 text-amber-500 shrink-0" />
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('accountLayout.helperTips')}</p>
              </div>
              <div className="space-y-1">
                {tips.map((tip) => (
                  <div key={tip.id} className="rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedTip(expandedTip === tip.id ? null : tip.id)}
                      className="w-full flex items-start justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-muted transition-colors"
                    >
                      <span className="font-medium text-foreground leading-snug">{tip.title}</span>
                      {expandedTip === tip.id
                        ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                        : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />}
                    </button>
                    {expandedTip === tip.id && (
                      <div className="px-3 pb-3 text-xs text-muted-foreground leading-relaxed bg-muted/30">
                        {tip.content}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {user?.uid && (
          <>
            <Separator className="my-3" />
            <p className="px-2 text-xs text-muted-foreground">UID: {user.uid}</p>
          </>
        )}
      </Card>

      {/* Content */}
      <div className="min-w-0 pt-1">
        <Outlet />
      </div>
    </div>
  );
};

export default AccountLayout;
